import React, { useState, useEffect } from 'react';
import { useWiki } from '../../contexts/WikiContext';
import { Clock, User, FileText, Filter, Download } from 'lucide-react';
import styles from './WikiHistory.module.css';

const WikiHistory = () => {
  const { getHistory, getAllPages } = useWiki();
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filters, setFilters] = useState({
    author: '',
    pageId: '',
    action: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    setLoadError('');
    setLoading(true);
    getHistory()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setHistory(list);
        setFilteredHistory(list);
      })
      .catch((err) => {
        setHistory([]);
        setFilteredHistory([]);
        const status = err?.status;
        const msg =
          status === 500
            ? 'Серверная ошибка (500). Проверьте логи бэкенда — в эндпоинте GET /api/wiki/history или в БД.'
            : err?.message || 'Не удалось загрузить историю';
        setLoadError(msg);
      })
      .finally(() => setLoading(false));
  }, [getHistory]);

  useEffect(() => {
    let filtered = [...history];
    if (filters.author) {
      filtered = filtered.filter((entry) =>
        (entry.author || '').toLowerCase().includes(filters.author.toLowerCase())
      );
    }
    if (filters.pageId) {
      filtered = filtered.filter((entry) => String(entry.pageId) === String(filters.pageId));
    }
    if (filters.action) {
      filtered = filtered.filter((entry) => entry.action === filters.action);
    }
    if (filters.startDate) {
      filtered = filtered.filter(
        (entry) => new Date(entry.timestamp || entry.createdAt) >= new Date(filters.startDate)
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(
        (entry) => new Date(entry.timestamp || entry.createdAt) <= new Date(filters.endDate)
      );
    }
    setFilteredHistory(filtered);
  }, [filters, history]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      author: '',
      pageId: '',
      action: '',
      startDate: '',
      endDate: '',
    });
  };

  const exportToCSV = () => {
    const headers = ['Дата', 'Автор', 'Страница', 'Действие'];
    const rows = filteredHistory.map((entry) => [
      formatDateTime(entry.timestamp || entry.createdAt),
      entry.author,
      entry.pageTitle,
      getActionLabel(entry.action),
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `wiki_history_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action) => {
    const labels = { create: 'Создание', update: 'Редактирование', delete: 'Удаление' };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = { create: '#10b981', update: '#0096ff', delete: '#ff6b6b' };
    return colors[action] || '#fff';
  };

  const pages = getAllPages();
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>История изменений</h1>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={exportToCSV}
            disabled={filteredHistory.length === 0}
          >
            <Download size={18} />
            Экспорт CSV
          </button>
        </div>
      </div>

      {loading && (
        <div className={styles.emptyState}>
          <p>Загрузка истории...</p>
        </div>
      )}

      {loadError && (
        <div className={styles.errorBlock}>
          <p>{loadError}</p>
          <p className={styles.errorHint}>
            Эндпоинт: GET /api/wiki/history?page=0&amp;size=100. При 500 смотрите логи приложения на сервере.
          </p>
        </div>
      )}

      {!loading && !loadError && (
      <>
      <div className={styles.filtersCard}>
        <div className={styles.filtersHeader}>
          <div className={styles.filtersTitle}>
            <Filter size={18} />
            <span>Фильтры</span>
          </div>
          {hasActiveFilters && (
            <button type="button" className={styles.smallButton} onClick={clearFilters}>
              Сбросить
            </button>
          )}
        </div>

        <div className={styles.filtersGrid}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Автор</label>
            <input
              type="text"
              placeholder="Поиск по автору..."
              value={filters.author}
              onChange={(e) => handleFilterChange('author', e.target.value)}
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Страница</label>
            <select
              value={filters.pageId}
              onChange={(e) => handleFilterChange('pageId', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">Все страницы</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Действие</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">Все действия</option>
              <option value="create">Создание</option>
              <option value="update">Редактирование</option>
              <option value="delete">Удаление</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>От даты</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>До даты</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className={styles.filterInput}
            />
          </div>
        </div>
      </div>

      {filteredHistory.length > 0 ? (
        <div className={styles.historyList}>
          {filteredHistory.map((entry) => (
            <div key={entry.id || `${entry.timestamp}-${entry.pageId}`} className={styles.historyItem}>
              <div
                className={styles.historyIcon}
                style={{ background: getActionColor(entry.action) }}
              >
                <FileText size={18} />
              </div>
              <div className={styles.historyContent}>
                <div className={styles.historyAction}>
                  <span
                    className={styles.actionBadge}
                    style={{
                      background: `${getActionColor(entry.action)}20`,
                      color: getActionColor(entry.action),
                    }}
                  >
                    {getActionLabel(entry.action)}
                  </span>
                  <span className={styles.historyPage}>{entry.pageTitle || '—'}</span>
                </div>
                <div className={styles.historyMeta}>
                  <span className={styles.metaItem}>
                    <User size={14} />
                    {entry.author || '—'}
                  </span>
                  <span className={styles.metaItem}>
                    <Clock size={14} />
                    {formatDateTime(entry.timestamp || entry.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>{hasActiveFilters ? 'Нет записей по заданным фильтрам' : 'История пуста'}</p>
          {hasActiveFilters && (
            <button type="button" className={styles.secondaryButton} onClick={clearFilters}>
              Сбросить фильтры
            </button>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default WikiHistory;
